import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { landingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Globe, ExternalLink, Trash2, Eye, Calendar, 
  ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';

export function LandingPagesListPage() {
  const { t, language } = useLanguage();
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await landingAPI.list();
      setPages(response.data);
    } catch (err) {
      console.error('Error loading pages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePublish = async (page) => {
    try {
      await landingAPI.update(page.page_id, { is_published: !page.is_published });
      setPages(prev => prev.map(p => 
        p.page_id === page.page_id ? { ...p, is_published: !p.is_published } : p
      ));
      toast.success(page.is_published ? t('landingPages.pageUnpublished') : t('landingPages.pagePublished'));
    } catch (err) {
      console.error('Error toggling page:', err);
      toast.error(t('common.error'));
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm(t('landingPages.deleteConfirm'))) return;
    
    try {
      await landingAPI.delete(pageId);
      setPages(prev => prev.filter(p => p.page_id !== pageId));
      toast.success(t('landingPages.pageDeleted'));
    } catch (err) {
      console.error('Error deleting page:', err);
      toast.error(t('common.error'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-pages-list">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">{t('landingPages.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('landingPages.description')}
          </p>
        </div>
        <Button asChild data-testid="new-landing-page-btn">
          <Link to="/landing-pages/new">
            <Plus className="mr-2 h-4 w-4" />
            {t('landingPages.newPage')}
          </Link>
        </Button>
      </div>

      {/* Pages Grid */}
      {pages.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="py-16 text-center">
            <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-bold mb-2">{t('landingPages.noPages')}</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t('landingPages.noPagesDesc')}
            </p>
            <Button asChild>
              <Link to="/landing-pages/new">
                <Plus className="mr-2 h-4 w-4" />
                {t('landingPages.createPage')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((page) => (
            <Card key={page.page_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-bold truncate">
                      {page.service || t('leads.service')} - {page.city || t('leads.city')}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      /{page.slug}
                    </CardDescription>
                  </div>
                  <Badge variant={page.is_published ? 'default' : 'secondary'}>
                    {page.is_published ? t('landingPages.published') : t('landingPages.draft')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* SEO Preview */}
                <div className="p-3 bg-muted/30 rounded-lg text-sm">
                  <p className="font-medium text-primary truncate">{page.seo?.title || t('landingPages.seoTitle')}</p>
                  <p className="text-muted-foreground text-xs line-clamp-2 mt-1">
                    {page.seo?.meta_description || t('landingPages.seoDescription')}
                  </p>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(page.created_at)}
                  </span>
                  <span>{t('landingPages.template')}: {page.template}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to={`/landing-pages/${page.page_id}`}>
                      {t('common.edit')}
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => togglePublish(page)}
                    title={page.is_published ? t('landingPages.unpublish') : t('landingPages.publish')}
                  >
                    {page.is_published ? (
                      <ToggleRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deletePage(page.page_id)}
                    title={t('common.delete')}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Box */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-6">
          <div className="flex items-start gap-4">
            <Globe className="h-8 w-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-bold mb-1">{t('landingPages.seoTip')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('landingPages.seoTipDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
